// src/scripts/userPrefs.js
// Per-user UI preferences — server-backed (cross-device) with localStorage
// mirror so reads are instant on subsequent loads. Use this for any UI state
// that should follow the user across browsers (group ordering, view choices,
// etc.) — not for transient state.
//
// API:
//   loadAllUserPrefs()        → fetch every pref for the current user, populate cache
//   getCachedPref(k, fb)      → sync read (cache → localStorage → fallback)
//   setPref(k, value)         → sync update of cache + localStorage; async push to server
//   clearAllUserPrefs()       → wipe in-memory cache + localStorage mirrors (call on logout)

import { api } from "./api";

const LS_PREFIX = "pref:";
const cache = new Map();
let loaded = false;
let loadPromise = null;

export async function loadAllUserPrefs() {
  if (loaded) return cache;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const { data } = await api.get("/user/preferences");
      for (const [k, v] of Object.entries(data || {})) {
        cache.set(k, v);
        try { localStorage.setItem(LS_PREFIX + k, JSON.stringify(v)); } catch {}
      }
      loaded = true;
    } catch {
      // Server unreachable / not authed — fall back to whatever's in localStorage.
    } finally {
      loadPromise = null;
    }
    return cache;
  })();

  return loadPromise;
}

export function getCachedPref(key, fallback = null) {
  if (cache.has(key)) return cache.get(key);
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (raw != null) {
      const parsed = JSON.parse(raw);
      cache.set(key, parsed);
      return parsed;
    }
  } catch {}
  return fallback;
}

// Tiny per-key debounce so rapid drag-reorders don't hammer the server.
const writeTimers = new Map();
const WRITE_DEBOUNCE_MS = 350;

export function setPref(key, value) {
  cache.set(key, value);
  try { localStorage.setItem(LS_PREFIX + key, JSON.stringify(value)); } catch {}

  // Server write — debounced
  clearTimeout(writeTimers.get(key));
  writeTimers.set(
    key,
    setTimeout(() => {
      api.put(`/user/preferences/${encodeURIComponent(key)}`, { value }).catch(() => {});
      writeTimers.delete(key);
    }, WRITE_DEBOUNCE_MS),
  );
}

export function clearAllUserPrefs() {
  // Wipe in-memory cache
  cache.clear();
  loaded = false;
  // Wipe localStorage mirrors
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {}
}
